import React, { FC } from 'react';

import { useIntl } from 'react-intl';

import { BaseStyleEnums, CoreUtils, IdEnums, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { PeriodDescriptor } from '~/core/types';
import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { PlayoffGameTeam, PlayoffSeriesTeam } from '../../../../types';

import { getGameStatusInfo } from './utils';

import {
    FinalGameStatus,
    GameInfoChip,
    Scoreboard,
    TeamContainer,
    TeamLogoContainer,
    TeamMatchupContainer,
    TeamName,
    TeamScore,
} from './styles';

type TeamMatchupProps = {
    awayTeam: PlayoffGameTeam;
    gameScheduleState: ShortCodeEnums.GameScheduleState;
    gameState: ShortCodeEnums.GameState;
    gameType: IdEnums.GameType;
    homeTeam: PlayoffGameTeam;
    bottomSeedTeam: PlayoffSeriesTeam;
    periodDescriptor: PeriodDescriptor;
    topSeedTeam: PlayoffSeriesTeam;
    startTimeUTC: string;
};

const TeamMatchup: FC<TeamMatchupProps> = ({
    awayTeam,
    gameScheduleState,
    gameState,
    gameType,
    homeTeam,
    bottomSeedTeam,
    periodDescriptor,
    topSeedTeam,
    startTimeUTC,
}) => {
    const intl = useIntl();
    const screenSize = useSizeWithThrottle();
    const isSmallScreen = screenSize.width < BaseStyleEnums.ScreenWidthBreakpoints.medium;

    // reliable way of checking for home and away team
    const matchingAwayTeam: PlayoffSeriesTeam | undefined = [bottomSeedTeam, topSeedTeam].find(
        ({ id }) => awayTeam.id === id
    );
    const matchingHomeTeam: PlayoffSeriesTeam | undefined = [bottomSeedTeam, topSeedTeam].find(
        ({ id }) => homeTeam.id === id
    );

    const didAwayTeamWin: boolean =
        awayTeam.score !== undefined && homeTeam.score !== undefined && awayTeam.score > homeTeam.score;
    const isTie = awayTeam.score === homeTeam.score;
    const isGameCritical = gameState === ShortCodeEnums.GameState.critical;
    const isGameInPost = CoreUtils.isPostGame(gameState);
    const fadeAwayTeam = !didAwayTeamWin && !isTie && isGameInPost;
    const fadeHomeTeam = didAwayTeamWin && !isTie && isGameInPost;

    return (
        <TeamMatchupContainer>
            <TeamContainer $fade={fadeAwayTeam}>
                <TeamName>
                    {isSmallScreen ? awayTeam.abbrev : parseValueFromLocalizedAttribute(awayTeam.commonName)}
                </TeamName>
                <TeamLogoContainer>
                    <TeamDisplay
                        displayName={false}
                        team={{ triCode: awayTeam.abbrev }}
                        logoUrlOverride={matchingAwayTeam?.darkLogo}
                        useInTable
                    />
                </TeamLogoContainer>
            </TeamContainer>
            <Scoreboard>
                <TeamScore $fade={fadeAwayTeam}>
                    <h5>{awayTeam.score}</h5>
                </TeamScore>
                {isGameInPost ? (
                    <FinalGameStatus>
                        {CoreUtils.getFinalGameStateDisplay(intl, gameType, periodDescriptor)}
                    </FinalGameStatus>
                ) : (
                    <GameInfoChip $isGameCritical={isGameCritical} $isGameLive={CoreUtils.isGameLive(gameState)}>
                        {getGameStatusInfo({
                            gameType,
                            gameScheduleState,
                            gameState,
                            intl,
                            periodDescriptor,
                            startTimeUTC,
                        })}
                    </GameInfoChip>
                )}
                <TeamScore $fade={fadeHomeTeam}>
                    <h5>{homeTeam.score}</h5>
                </TeamScore>
            </Scoreboard>
            <TeamContainer $fade={fadeHomeTeam} $isRight>
                <TeamLogoContainer>
                    <TeamDisplay
                        displayName={false}
                        team={{ triCode: homeTeam.abbrev }}
                        logoUrlOverride={matchingHomeTeam?.darkLogo}
                        useInTable
                    />
                </TeamLogoContainer>
                <TeamName>
                    {isSmallScreen ? homeTeam.abbrev : parseValueFromLocalizedAttribute(homeTeam.commonName)}
                </TeamName>
            </TeamContainer>
        </TeamMatchupContainer>
    );
};

export default TeamMatchup;
